<script setup lang="ts">
  import { ref, watch, nextTick } from 'vue'
  import type { AiPromptAsset, TargetPromptAiModel } from '../model/types'
  import PromptAssetSelect from './PromptAssetSelect.vue'
  import ThinkingModelSelect from './ThinkingModelSelect.vue'

  defineProps<{
    loading?: boolean
    assets: AiPromptAsset[]
    canManageAssets: boolean
    canOverrideModel: boolean
  }>()

  const text = defineModel<string>('text', {
    required: true,
    default: ''
  })

  const selectedAssetId = defineModel<string | undefined>('selectedAssetId', {
    required: true
  })

  const selectedThinkingModel = defineModel<TargetPromptAiModel | undefined>(
    'selectedThinkingModel',
    { required: true }
  )

  const emit = defineEmits<{
    (e: 'add-asset'): void
    (e: 'edit-asset', asset: AiPromptAsset): void
  }>()

  const textareaRef = ref<HTMLTextAreaElement | null>(null)

  const autoResize = () => {
    const el = textareaRef.value
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  watch(
    text,
    async () => {
      await nextTick()
      autoResize()
    },
    { immediate: true }
  )
</script>

<template>
  <div class="mx-5 flex-1 rounded-lg border">
    <textarea
      ref="textareaRef"
      v-model="text"
      :disabled="loading"
      class="min-h-[100px] w-full resize-none p-3 focus:outline-none focus:ring-0"
      @input="autoResize"
    />
    <div class="flex items-center gap-2 p-3">
      <div class="flex w-fit flex-row gap-2">
        <PromptAssetSelect
          v-if="canManageAssets || assets.length > 1"
          v-model:selected-asset-id="selectedAssetId"
          :assets="assets"
          :can-manage-assets="canManageAssets"
          class="col-span-2"
          @add-asset="emit('add-asset')"
          @edit-asset="emit('edit-asset', $event)"
        />
        <ThinkingModelSelect
          v-if="canOverrideModel"
          v-model="selectedThinkingModel"
          class="col-span-1"
          allow-default
          required
        />
      </div>
      <slot name="actions" />
    </div>
  </div>
</template>
